import { MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react';
import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { IconButton } from 'src/components';
import { color } from 'src/constants/color.css';
import { useDebouncedCallback } from 'use-debounce';

import { Style } from '../../../constants';

type Props = {
  value: string;
  /**
   * NB: this is debounced
   */
  onChange: (value: string) => void;
  placeholder?: string;
};

export default function SearchInput({
  value,
  onChange,
  placeholder = 'Search tickets...',
}: Props) {
  const [localValue, setLocalValue] = React.useState(value);
  const [expanded, setExpanded] = React.useState(!!value);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const debouncedOnChange = useDebouncedCallback(onChange, 300);

  React.useEffect(() => {
    setLocalValue(value);
    if (value) setExpanded(true);
  }, [value]);

  React.useEffect(() => {
    if (expanded) {
      inputRef.current?.focus();
    }
  }, [expanded]);

  React.useEffect(() => {
    if (!expanded) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        !localValue
      ) {
        setExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expanded, localValue]);

  if (!expanded) {
    return (
      <IconButton
        tooltip="Search"
        onClick={() => setExpanded(true)}
        aria-label="Search tickets"
      >
        <MagnifyingGlassIcon size={20} />
      </IconButton>
    );
  }

  return (
    <html.div
      ref={containerRef}
      style={[Style.row, Style.alignCenter, styles.container]}
    >
      <html.div style={[Style.centerBoth, styles.searchIconLeft]}>
        <MagnifyingGlassIcon size={20} color={color.neutral80} />
      </html.div>

      <html.input
        ref={inputRef}
        style={styles.input}
        value={localValue}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setLocalValue(e.target.value);
          debouncedOnChange(e.target.value);
        }}
        placeholder={placeholder}
      />

      {localValue && (
        <html.button
          style={[Style.row, Style.centerBoth, styles.clearButton]}
          onClick={() => {
            setLocalValue('');
            debouncedOnChange.cancel();
            onChange('');
            inputRef.current?.focus();
          }}
          aria-label="Clear search"
        >
          <XIcon size={14} color={color.neutral80} weight="bold" />
        </html.button>
      )}
    </html.div>
  );
}

const styles = css.create({
  container: {
    position: 'relative',
    width: {
      default: 310,
      '@media (max-width: 768px)': '100%',
    },
  },
  searchIconLeft: {
    position: 'absolute',
    left: 12,
    pointerEvents: 'none',
  },
  input: {
    width: '100%',
    height: 40,
    paddingInlineStart: 40,
    paddingInlineEnd: 36,
    borderWidth: 0,
    borderRadius: 8,
    fontSize: 14,
    fontFamily: 'Inter, sans-serif',
    lineHeight: 1.5,
    color: color.neutral100,
    backgroundColor: color.neutral0,
    outline: 'none',
    '::placeholder': {
      color: color.neutral40,
    },
    ':focus': {
      borderColor: color.neutral60,
    },
  },
  clearButton: {
    position: 'absolute',
    right: 8,
    padding: 4,
    backgroundColor: 'transparent',
    borderWidth: 0,
    borderRadius: 20,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: color.neutral15,
    },
  },
});
